import { JSX } from 'react';
import { ContentContainer, Spinner } from '@ahc-digital/component-library';
import {
  ComponentRendering,
  constants,
  GetServerSideComponentProps,
  LayoutServiceData,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Script from 'next/script';
import { useState } from 'react';
import { sparkleUtils } from 'src/shared/sparkle-utils';
import { useSparkleApi } from 'src/shared/SparkleApi.service';
import styles from './I04SparkleLocationOverview.module.scss';
import { I04SparkleLocationOverviewProps } from './I04SparkleLocationOverview.types';
import { ImportedMetaData } from 'src/shared/ImportedMetaData';
import { GetServerSidePropsContext } from 'next';
import { ImportedData } from 'lib/imported-data/ImportedData.types';
import { ImportedDataProviderFactory } from 'lib/imported-data';
import { useCreateMarketingRenderingHame } from 'src/shared/useCreateMarketingRenderingName';
import { SparkleLocationImportedDataRequest } from '@/lib/imported-data/ImportedDataRequest.types';

export const Default = (props?: I04SparkleLocationOverviewProps): JSX.Element => {
  const sparkleApi = useSparkleApi();
  const { sitecoreContext } = useSitecoreContext();
  const [isError, setIsError] = useState(false);
  const marketingRenderingName = useCreateMarketingRenderingHame(
    props?.rendering?.componentName as string
  );

  const isWildcardPage = sitecoreContext.route?.name === '*';

  const applyMetadata = async (providerSlug: string) => {
    const { isSuccess, textContent: metaDataHtml } = await sparkleApi.fetchLocationMetadata(
      providerSlug
    );

    if (!isSuccess) {
      setIsError(true);
    }

    const doc = new DOMParser().parseFromString(metaDataHtml, 'text/html');

    // Replace metadata tags
    [
      'title',
      'meta[name="description"]',
      'meta[name="keywords"]',
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[property="og:locale"]',
      'meta[property="og:site_name"]',
      'meta[property="og:type"]',
      'meta[property="og:image"]',
      'meta[property="og:image:width"]',
      'meta[property="og:image:height"]',
      'meta[property="article:section"]',
      'meta[property="og:url"]',
      'link[rel="canonical"]',
      'meta[name="breadcrumb"]',
      'script[type="application/ld+json"]',
    ].forEach((selector: string) => {
      const pageNode = document.querySelector(selector);
      const snippetNode = doc.querySelector(selector);
      if (pageNode && snippetNode) {
        pageNode.replaceWith(snippetNode.cloneNode(true));
        snippetNode.remove();
      }
    });

    // Replace breadcrumb page title
    const breadcrumbPage = document.querySelector('[data-testid="breadcrumb-page"]');
    const newTitle = document.querySelector('meta[name="breadcrumb"]');
    if (newTitle && breadcrumbPage) {
      breadcrumbPage.textContent = newTitle.getAttribute('content');
    }

    const head = document.head || document.getElementsByTagName('head')[0];
    doc.head.childNodes.forEach((node) => {
      head.appendChild(node.cloneNode(true));
    });
  };

  const applyContent = async (locationSlug: string) => {
    const providerContentDiv = document.getElementById('provider-content');
    if (!providerContentDiv) return;

    const { isSuccess, textContent: htmlContent } = await sparkleApi.fetchLocationContent(
      locationSlug
    );

    if (!isSuccess) {
      setIsError(true);
      return;
    }

    sparkleUtils.applyHtmlSnippet(htmlContent, providerContentDiv);
  };

  return (
    <>
      <ImportedMetaData data={props?.importedData}></ImportedMetaData>
      <Script
        src="https://code.jquery.com/jquery-3.7.1.min.js"
        integrity="sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo="
        crossOrigin="anonymous"
        onError={() => setIsError(true)}
        onReady={() => {
          let locationSlug = window.location.pathname.split('/').pop();
          if (!locationSlug) return;

          locationSlug = locationSlug.split('?')[0];
          locationSlug = locationSlug.replaceAll(/[\?\#]/g, '');

          if (isWildcardPage) {
            applyMetadata(locationSlug);
          }
          applyContent(locationSlug);
        }}
      />
      <div
        id="provider-content"
        className={styles['provider-content']}
        data-component={marketingRenderingName}
      >
        {isError ? (
          <ContentContainer
            headline="Something went wrong"
            headlineTag="h3"
            spacing="40-24"
            supportCopy={<p>There was an error loading this location. Please try again later.</p>}
          />
        ) : (
          <ContentContainer spacing="40-24">
            <Spinner />
          </ContentContainer>
        )}
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideComponentProps = async (
  _rendering: ComponentRendering,
  _layoutData: LayoutServiceData,
  context: GetServerSidePropsContext
) => {
  if (process.env.JSS_MODE === constants.JSS_MODE.DISCONNECTED) {
    return { importedData: {} as ImportedData };
  }
  const slug = sparkleUtils.getSlug(context.req.url ?? '');
  if (sparkleUtils.isEmptyOrWhiteSpace(slug)) return;

  const provider = ImportedDataProviderFactory.create();
  const request = new SparkleLocationImportedDataRequest(slug, context.req.headers.host ?? '');
  const result = await provider.getDataByRequest(request);
  return { importedData: result };
};
